import type { ReactNode } from "react";
import { type GetProps, isWeb } from "tamagui";
import type { ContainerLayoutProps } from "../../utils/layout";
import { webPointerProps } from "../interactive/styles";
import { type OptionalInteractiveProps, useInteractiveElement } from "../interactive/useInteractiveElement";
import { type Appear, motionTransition, type Press } from "../Motion/motion";
import { StyledCard } from "./StyledCard";
export type CardVariant = "outlined" | "elevated";
export type CardSize = "small" | "large";

// Omit hook-managed props so consumers can't fight `useInteractiveElement`'s
// output. `role` / `aria-checked` / `aria-expanded` stay open — selection
// variants set them.
type StyledCardProps = Omit<GetProps<typeof StyledCard>, "interactive" | "tabIndex" | "onKeyDown" | "aria-disabled" | "press" | "appear">;
type CommonCardProps = StyledCardProps & ContainerLayoutProps & {
  variant?: CardVariant;
  size?: CardSize;
  /** Press feedback when interactive. @default "default" */
  press?: Press;
  /** Enter / exit shape; wrap in `AnimatePresence` for the exit half. */
  appear?: Appear;
  children?: ReactNode;
  /** Required only when visible content doesn't describe the action (WCAG 2.5.3). */
  "aria-label"?: string;
};
export type CardProps = CommonCardProps & OptionalInteractiveProps;
export const Card = ({
  kind,
  onPress,
  href,
  replace,
  scroll,
  shallow,
  disabled,
  haptic,
  press = "default",
  appear,
  ...rest
}: CardProps) => {
  const {
    isInteractive,
    renderTag,
    interactiveProps
  } = useInteractiveElement({
    kind,
    onPress,
    href,
    replace,
    scroll,
    shallow,
    disabled,
    haptic
  });
  const resolvedPress = isInteractive && !disabled ? press : "none";
  return <StyledCard
  // Re-key on render-tag swaps so the animated ref remounts.
  key={renderTag ?? "static"} interactive={isInteractive} disabled={disabled} press={resolvedPress} appear={appear} {...motionTransition(isInteractive ? press : "none", appear)} {...isInteractive && !disabled ? webPointerProps : {}} {...renderTag ? {
    render: renderTag
  } : {}} {...interactiveProps} style={isWeb && renderTag === "a" ? {
    textDecoration: "none"
  } : undefined} {...rest} data-sentry-element="StyledCard" data-sentry-component="Card" data-sentry-source-file="Card.tsx" />;
};